/**
 * UnresolvedDeskStubs — #572.
 *
 * A live front-desk charge whose gateway outcome was INDETERMINATE (a
 * timeout — the gateway MAY have captured) or DUPLICATE leaves a KEPT
 * pending Payment stub. That stub never shows in Transactions (which lists
 * completed/refunded only), and no charge/refund endpoint can resolve it —
 * so from then on every desk collection path for that booking 409s
 * (charge_unresolved) and the booking is stranded. Before this surface,
 * recovery required direct DB surgery.
 *
 * This worklist reads GET /reconciliation/pending-stubs and lets staff CLOSE
 * each stub after checking the gateway dashboard:
 *
 *   - Mark collected → the gateway DID charge: enter the real transaction id
 *     (from the dashboard); the server pins it and settles the invoice.
 *   - No charge → release: the gateway did NOT charge; the stub is deleted
 *     and the booking's collection lane re-opens.
 *
 * Renders nothing when the list is empty (the healthy steady state), so the
 * Payments page carries no dead chrome. The resolve actions are gated on
 * payments-ops:write:all (the server enforces it regardless — this is a UX
 * courtesy so floor roles don't see a control they can't use).
 */
import { useCallback, useEffect, useState } from 'react';
import { adminPaymentsApi, type AdminPendingStub } from '../../lib/api.js';
import { PermissionGate } from '../PermissionGate.js';

type UnresolvedDeskStubsProps = {
  readonly apiUrl: string;
  /** Parent bumps this to force a refetch after an external money event. */
  readonly refreshKey?: number;
  /** Called after a stub is successfully resolved so parents can refresh. */
  readonly onResolved?: () => void;
};

type LoadState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; stubs: AdminPendingStub[] };

/** Per-row "mark collected" form state (the transaction-id capture). */
type CompleteForm = {
  paymentId: string;
  transactionId: string;
};

function formatMoney(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

function formatDateTime(iso: string): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

export function UnresolvedDeskStubs({
  apiUrl,
  refreshKey = 0,
  onResolved,
}: UnresolvedDeskStubsProps): JSX.Element | null {
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [busyId, setBusyId] = useState<string | null>(null);
  const [completing, setCompleting] = useState<CompleteForm | null>(null);
  const [confirmReleaseId, setConfirmReleaseId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionOk, setActionOk] = useState<string | null>(null);

  const load = useCallback(async () => {
    setState({ kind: 'loading' });
    const res = await adminPaymentsApi.getPendingStubs(apiUrl);
    if (res.error || !res.data) {
      setState({
        kind: 'error',
        message: res.error ?? 'Could not check for unresolved charges',
      });
      return;
    }
    setState({ kind: 'ready', stubs: res.data.stubs ?? [] });
  }, [apiUrl]);

  useEffect(() => {
    void load();
  }, [load, refreshKey]);

  const afterMutation = useCallback(async () => {
    await load();
    onResolved?.();
  }, [load, onResolved]);

  const doComplete = useCallback(async () => {
    if (!completing) return;
    const txn = completing.transactionId.trim();
    if (!txn) {
      setActionError(
        'Enter the gateway transaction id (from the gateway dashboard) to mark this charge collected.',
      );
      return;
    }
    setBusyId(completing.paymentId);
    setActionError(null);
    setActionOk(null);
    const res = await adminPaymentsApi.resolvePendingStub(
      apiUrl,
      completing.paymentId,
      { action: 'complete', gatewayTransactionId: txn },
    );
    setBusyId(null);
    if (res.error || !res.data?.resolution) {
      setActionError(res.error ?? 'Could not mark the charge collected');
      return;
    }
    setActionOk(
      'Charge marked collected — the payment is recorded and its invoice settled.',
    );
    setCompleting(null);
    await afterMutation();
  }, [apiUrl, completing, afterMutation]);

  const doRelease = useCallback(
    async (paymentId: string) => {
      setBusyId(paymentId);
      setActionError(null);
      setActionOk(null);
      const res = await adminPaymentsApi.resolvePendingStub(apiUrl, paymentId, {
        action: 'release',
      });
      setBusyId(null);
      setConfirmReleaseId(null);
      if (res.error || !res.data?.resolution) {
        setActionError(res.error ?? 'Could not release the charge');
        return;
      }
      setActionOk(
        'Charge released — the booking can be collected again from the desk.',
      );
      await afterMutation();
    },
    [apiUrl, afterMutation],
  );

  const stubs = state.kind === 'ready' ? state.stubs : [];

  // Healthy steady state — no chrome. Errors DO render (an invisible
  // reconciliation read failure would defeat the surface's purpose).
  if (state.kind === 'ready' && stubs.length === 0 && !actionError && !actionOk) {
    return null;
  }
  if (state.kind === 'loading') return null;

  return (
    <article
      className="ferro-card"
      aria-label="Unresolved charges"
      data-testid="pending-stubs"
    >
      <header className="ferro-card__header">
        <h2 className="ferro-section__subtitle">
          Unresolved charges — needs reconciliation
        </h2>
        {state.kind === 'ready' && stubs.length > 0 ? (
          <span className="ferro-badge ferro-badge--warning">
            {stubs.length}
          </span>
        ) : null}
        <button
          type="button"
          className="ferro-btn ferro-btn--ghost"
          onClick={() => void load()}
          data-testid="pending-stubs-refresh"
        >
          Refresh
        </button>
      </header>

      {state.kind === 'error' ? (
        <div className="ferro-dashboard__error" role="alert">
          <p>{state.message}</p>
          <button
            type="button"
            className="ferro-btn ferro-btn--ghost"
            onClick={() => void load()}
          >
            Retry
          </button>
        </div>
      ) : null}

      {actionError ? (
        <div
          className="ferro-dashboard__error"
          role="alert"
          data-testid="pending-stubs-action-error"
        >
          <p>{actionError}</p>
          <button
            type="button"
            className="ferro-btn ferro-btn--ghost"
            onClick={() => setActionError(null)}
          >
            Dismiss
          </button>
        </div>
      ) : null}

      {actionOk ? (
        <div
          className="ferro-banner ferro-banner--success"
          role="status"
          data-testid="pending-stubs-action-ok"
        >
          <p>{actionOk}</p>
          <button
            type="button"
            className="ferro-btn ferro-btn--ghost"
            onClick={() => setActionOk(null)}
          >
            Dismiss
          </button>
        </div>
      ) : null}

      {state.kind === 'ready' && stubs.length > 0 ? (
        <>
          <p className="ferro-muted">
            These front-desk charges timed out — the gateway may or may not
            have captured. Check the transaction in your gateway dashboard,
            then mark it collected (enter its transaction id) or release it if
            no charge went through. Until then, the booking can’t be collected
            again.
          </p>
          <ul className="ferro-table" aria-label="Unresolved charge list">
            {stubs.map((s) => {
              const isCompleting = completing?.paymentId === s.paymentId;
              const isConfirmingRelease = confirmReleaseId === s.paymentId;
              return (
                <li
                  key={s.paymentId}
                  className="ferro-table__row"
                  data-testid={`pending-stub-${s.paymentId}`}
                >
                  <div className="ferro-table__body">
                    <div className="ferro-table__primary">
                      {s.customerName ||
                        `Customer ${s.customerId.slice(0, 8)}`}
                      {' · '}
                      {formatMoney(s.totalCents)}
                      {s.tipCents > 0
                        ? ` (incl. ${formatMoney(s.tipCents)} tip)`
                        : ''}
                    </div>
                    <div className="ferro-table__meta">
                      {s.serviceTypeName || 'Service'}
                      {s.bookingId ? ` · booking #${s.bookingId.slice(0, 8)}` : ''}
                      {' · '}
                      {formatDateTime(s.createdAt)}
                    </div>
                  </div>

                  <div className="ferro-table__actions">
                    <PermissionGate permission="payments-ops:write:all">
                      {isConfirmingRelease ? (
                        <span
                          className="ferro-inline-confirm"
                          data-testid={`release-confirm-${s.paymentId}`}
                        >
                          <span className="ferro-muted">
                            No charge went through?
                          </span>
                          <button
                            type="button"
                            className="ferro-btn ferro-btn--danger"
                            disabled={busyId === s.paymentId}
                            onClick={() => void doRelease(s.paymentId)}
                            data-testid={`release-confirm-yes-${s.paymentId}`}
                          >
                            {busyId === s.paymentId
                              ? 'Releasing…'
                              : 'Confirm release'}
                          </button>
                          <button
                            type="button"
                            className="ferro-btn ferro-btn--ghost"
                            onClick={() => setConfirmReleaseId(null)}
                          >
                            Cancel
                          </button>
                        </span>
                      ) : isCompleting ? null : (
                        <>
                          <button
                            type="button"
                            className="ferro-btn ferro-btn--primary"
                            onClick={() => {
                              setActionError(null);
                              setActionOk(null);
                              setConfirmReleaseId(null);
                              setCompleting({
                                paymentId: s.paymentId,
                                transactionId: '',
                              });
                            }}
                            data-testid={`complete-${s.paymentId}`}
                          >
                            Mark collected
                          </button>
                          <button
                            type="button"
                            className="ferro-btn ferro-btn--ghost"
                            onClick={() => {
                              setActionError(null);
                              setActionOk(null);
                              setCompleting(null);
                              setConfirmReleaseId(s.paymentId);
                            }}
                            data-testid={`release-${s.paymentId}`}
                          >
                            No charge — release
                          </button>
                        </>
                      )}
                    </PermissionGate>
                  </div>

                  {isCompleting && completing ? (
                    <form
                      className="ferro-refund-form"
                      data-testid={`complete-form-${s.paymentId}`}
                      noValidate
                      onSubmit={(e) => {
                        e.preventDefault();
                        void doComplete();
                      }}
                    >
                      <p className="ferro-muted">
                        Enter the gateway transaction id for this{' '}
                        {formatMoney(s.totalCents)} charge (copy it from the
                        gateway dashboard).
                      </p>
                      <label className="ferro-field">
                        <span>Gateway transaction id</span>
                        <input
                          type="text"
                          maxLength={64}
                          value={completing.transactionId}
                          onChange={(e) =>
                            setCompleting({
                              ...completing,
                              transactionId: e.target.value,
                            })
                          }
                          placeholder="e.g. 60123456789"
                          data-testid={`complete-txn-${s.paymentId}`}
                        />
                      </label>
                      <div className="ferro-table__actions">
                        <button
                          type="submit"
                          className="ferro-btn ferro-btn--primary"
                          disabled={busyId === s.paymentId}
                          data-testid={`complete-submit-${s.paymentId}`}
                        >
                          {busyId === s.paymentId
                            ? 'Recording…'
                            : 'Mark collected'}
                        </button>
                        <button
                          type="button"
                          className="ferro-btn ferro-btn--ghost"
                          onClick={() => setCompleting(null)}
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </>
      ) : null}
    </article>
  );
}
